<script context="module" lang="ts">
  const dates: MonthData[] = getCalendarDates();
</script>

<script lang="ts">
  import { isFuture } from "date-fns";
  import { fade } from "svelte/transition";
  import type { MonthData, FilteringDate } from "../../helpers/types";
  import {
    getCalendarDates,
    getLastMonthRange,
    getLastQuarterRange,
    getLastWeekRange,
    getLastYearRange,
    getPast14DaysRange,
    getPast7DaysRange,
    isSameDay,
    yearMonthDay,
  } from "../../helpers/utils";
  import ButtonSmall from "../ButtonSmall.svelte";
  import CalendarItem from "../CalendarItem.svelte";
  import Cell from "../Cell.svelte";
  import Text from "../Text.svelte";
  import { track } from "../../helpers/mixpanel";
  import { store } from "../../helpers/store";
  import {
    type DatePickerStore,
    type FilteringDateChoice,
    type PickerState,
  } from "../../stores/date-picker";
  import { portal } from "../../actions/portal";
  import { DEFAULT_FADE_TRANSITION } from "../modals/transitions";
  import ModalUnderlay from "../modals/ModalUnderlay.svelte";
  import type { ModalPartAttributes } from "../modals/createModal";
  import { createVirtualizer } from "@tanstack/svelte-virtual";
  import DateRangeDisplayButton from "../DateRangeDisplayButton.svelte";
  import { tick } from "svelte";
  import { formatDayShortMonthYear, locale } from "../../helpers/localise";
  import { selectedBuyer } from "../../stores/buyer";

  $: suppliers = $store.suppliers;

  const presets = [
    {
      title: "today",
      onPress: () => {
        const today = new Date();
        onChange(today, today);
        track("overview_select_date_range_today", {
          start_date: yearMonthDay(today),
          end_date: yearMonthDay(today),
          supplier_id: $store.selectedSupplier?.id || "all",
          supplier_name: $store.selectedSupplier?.name || "all",
          buyer_id: $selectedBuyer?.id,
          buyer_name: $selectedBuyer?.name,
          pro_count: suppliers.filter((s) => s.buyer_id === $selectedBuyer?.id && s.pro).length,
        });
      },
    },
    {
      title: "last 7 days",
      onPress: () => {
        const range = getPast7DaysRange();
        onChange(range.start, range.end);
        track("overview_select_date_range_past_7_days", {
          start_date: yearMonthDay(range.start),
          end_date: yearMonthDay(range.end),
          supplier_id: $store.selectedSupplier?.id || "all",
          supplier_name: $store.selectedSupplier?.name || "all",
          buyer_id: $selectedBuyer?.id,
          buyer_name: $selectedBuyer?.name,
          pro_count: suppliers.filter((s) => s.buyer_id === $selectedBuyer?.id && s.pro).length,
        });
      },
    },
    {
      title: "last 14 days",
      onPress: () => {
        const range = getPast14DaysRange();
        onChange(range.start, range.end);
        track("overview_select_date_range_past_14_days", {
          start_date: yearMonthDay(range.start),
          end_date: yearMonthDay(range.end),
          supplier_id: $store.selectedSupplier?.id || "all",
          supplier_name: $store.selectedSupplier?.name || "all",
          buyer_id: $selectedBuyer?.id,
          buyer_name: $selectedBuyer?.name,
          pro_count: suppliers.filter((s) => s.buyer_id === $selectedBuyer?.id && s.pro).length,
        });
      },
    },
    {
      title: "last week",
      onPress: () => {
        const range = getLastWeekRange();
        onChange(range.start, range.end);
        track("overview_select_date_range_last_week", {
          start_date: yearMonthDay(range.start),
          end_date: yearMonthDay(range.end),
          supplier_id: $store.selectedSupplier?.id || "all",
          supplier_name: $store.selectedSupplier?.name || "all",
          buyer_id: $selectedBuyer?.id,
          buyer_name: $selectedBuyer?.name,
          pro_count: suppliers.filter((s) => s.buyer_id === $selectedBuyer?.id && s.pro).length,
        });
      },
    },
    {
      title: "last month",
      onPress: () => {
        const range = getLastMonthRange();
        onChange(range.start, range.end);
        track("overview_select_date_range_last_month", {
          start_date: yearMonthDay(range.start),
          end_date: yearMonthDay(range.end),
          supplier_id: $store.selectedSupplier?.id || "all",
          supplier_name: $store.selectedSupplier?.name || "all",
          buyer_id: $selectedBuyer?.id,
          buyer_name: $selectedBuyer?.name,
          pro_count: suppliers.filter((s) => s.buyer_id === $selectedBuyer?.id && s.pro).length,
        });
      },
    },
    {
      title: "last quarter",
      onPress: () => {
        const range = getLastQuarterRange();
        onChange(range.start, range.end);
        track("overview_select_date_range_last_quarter", {
          start_date: yearMonthDay(range.start),
          end_date: yearMonthDay(range.end),
          supplier_id: $store.selectedSupplier?.id || "all",
          supplier_name: $store.selectedSupplier?.name || "all",
          buyer_id: $selectedBuyer?.id,
          buyer_name: $selectedBuyer?.name,
          pro_count: suppliers.filter((s) => s.buyer_id === $selectedBuyer?.id && s.pro).length,
        });
      },
    },
    {
      title: "last year",
      onPress: () => {
        const range = getLastYearRange();
        onChange(range.start, range.end);
        track("overview_select_date_range_last_year", {
          start_date: yearMonthDay(range.start),
          end_date: yearMonthDay(range.end),
          supplier_id: $store.selectedSupplier?.id || "all",
          supplier_name: $store.selectedSupplier?.name || "all",
          buyer_id: $selectedBuyer?.id,
          buyer_name: $selectedBuyer?.name,
          pro_count: suppliers.filter((s) => s.buyer_id === $selectedBuyer?.id && s.pro).length,
        });
      },
    },
  ];

  export let pickerState: PickerState;
  export let dateRangeDisplay: string = "";
  export let filteringDateDisplay: string = "";
  export let triggerElement: HTMLElement | null = null;
  export let onOpen: () => void;
  export let onCancel: () => void;
  export let onDateSelect: (date: Date) => void;
  export let underlayAttrs: ModalPartAttributes | undefined = undefined;
  export let content: DatePickerStore["datePickerContent"];
  export let onChange: (startDate: Date, endDate: Date) => void;
  export let onConfirm: () => void;
  export let startDate: Date;
  export let endDate: Date;
  export let filteringDateChoices: FilteringDateChoice[] | undefined = [];
  // the selected `value` from filteringDateChoices.
  // its own parameter so it can be bound to.
  export let filteringDate: FilteringDate | null = null;
  let virtualListEl: HTMLDivElement;
  $: virtualizer = createVirtualizer<HTMLDivElement, HTMLDivElement>({
    count: dates.length,
    getScrollElement: () => virtualListEl,
    estimateSize: (i) => (dates[i].dates.length === 6 ? 288 : 245),
    overscan: 2,
  });

  $: virtualDateItems = $virtualizer.getVirtualItems();

  function isSelected(date: Date, valid: boolean, type: string, startDate: Date, endDate: Date) {
    if (startDate && endDate) {
      var rangeStart = startDate.getTime();
      var rangeEnd = endDate.getTime();
      var toCheck = date.getTime();

      if (!valid) {
        if (type === "pre") {
          return rangeStart <= toCheck && rangeEnd > toCheck;
        } else if (type === "post") {
          return rangeStart < toCheck && rangeEnd > toCheck;
        }
      }

      return toCheck >= rangeStart && toCheck <= rangeEnd;
    }

    if (startDate && !endDate) {
      return valid ? isSameDay(date, startDate) : false;
    }

    return false;
  }

  async function handleOpen() {
    onOpen();
    await tick();
    $virtualizer.scrollToIndex(dates.length - 1);
  }
</script>

<DateRangeDisplayButton
  on:click={handleOpen}
  dateRange={dateRangeDisplay}
  filteringDate={filteringDateDisplay}
  bind:element={triggerElement}
/>

<div use:portal>
  {#if pickerState !== "idle"}
    <ModalUnderlay {...underlayAttrs} />
    <div
      use:content
      class="container"
      style="left: calc(50% - 278px)"
      transition:fade|global={DEFAULT_FADE_TRANSITION}
    >
      <div class="presets">
        {#if filteringDateChoices?.length}
          <div class="filters">
            <fieldset>
              <legend><Text ocd size="small">filter by</Text></legend>
              {#each filteringDateChoices as { value, display }}
                <!-- both of the following elements need cursor-oversized, the UA has strong feelings
              about their cursors -->
                <label class="preset-row preset-text cursor-oversized">
                  <input
                    type="radio"
                    class="cursor-oversized"
                    name="filter-date"
                    {value}
                    bind:group={filteringDate}
                  />
                  <Text ocd size="small">{display}</Text>
                </label>
              {/each}
            </fieldset>
          </div>
        {/if}
        {#each presets as preset}
          <button class="preset-row cursor-oversized" on:click={preset.onPress}>
            <div class="preset-text">
              <Text ocd size="small">{preset.title}</Text>
            </div>
          </button>
        {/each}
      </div>
      <div class="calendar-container">
        <div class="header">
          <div class="selected-date">
            <Text size="tiny" ocd color="transparency-dark">from</Text>
            <Text size="medium">{formatDayShortMonthYear(startDate, $locale)}</Text>
          </div>
          <div class="selected-date">
            <Text size="tiny" ocd color="transparency-dark">until</Text>
            <Text size="medium">{formatDayShortMonthYear(endDate, $locale)}</Text>
          </div>
        </div>
        <div class="calendar">
          <div class="calendar-header">
            <Cell align="center" style="flex:1;">
              <Text size="tiny" ocd color="transparency-dark">mon</Text>
            </Cell>
            <Cell align="center" style="flex:1;">
              <Text size="tiny" ocd color="transparency-dark">tue</Text>
            </Cell>
            <Cell align="center" style="flex:1;">
              <Text size="tiny" ocd color="transparency-dark">wed</Text>
            </Cell>
            <Cell align="center" style="flex:1;">
              <Text size="tiny" ocd color="transparency-dark">thu</Text>
            </Cell>
            <Cell align="center" style="flex:1;">
              <Text size="tiny" ocd color="transparency-dark">fri</Text>
            </Cell>
            <Cell align="center" style="flex:1;">
              <Text size="tiny" ocd color="transparency-dark">sat</Text>
            </Cell>
            <Cell align="center" style="flex:1;">
              <Text size="tiny" ocd color="transparency-dark">sun</Text>
            </Cell>
          </div>
          <div class="calendar-content" id="calendar-content" bind:this={virtualListEl}>
            <div style="position: relative; height: {$virtualizer.getTotalSize()}px; width: 100%;">
              <div
                style="position: absolute; top: 0; left: 0; width: 100%; transform: translateY({virtualDateItems[0]
                  ? virtualDateItems[0].start
                  : 0}px);"
              >
                {#each virtualDateItems as month (month.index)}
                  <div class="month-container" data-index={month.index}>
                    <div class="month">
                      <Text size="medium" bold>{dates[month.index].header}</Text>
                    </div>
                    {#each dates[month.index].dates as week}
                      <div class="week-row">
                        {#each week as day}
                          <CalendarItem
                            date={day.date}
                            text={day.dayOfMonth}
                            valid={day.valid}
                            disabled={isFuture(day.date)}
                            selected={isSelected(
                              day.check,
                              day.valid,
                              day.type,
                              startDate,
                              endDate
                            )}
                            isFirst={day.valid && isSameDay(day.date, startDate)}
                            isLast={(day.valid && isSameDay(day.date, endDate)) ||
                              (!endDate && isSameDay(day.date, startDate))}
                            didSelect={onDateSelect}
                          />
                        {/each}
                      </div>
                    {/each}
                  </div>
                {/each}
              </div>
            </div>
          </div>
          <div class="footer">
            <div class="cancel">
              <ButtonSmall color="neutral-02" on:click={onCancel}
                ><Text ocd>cancel</Text></ButtonSmall
              >
            </div>
            <ButtonSmall color="dark" disabled={!startDate && !endDate} on:click={onConfirm}
              ><Text ocd>set date</Text></ButtonSmall
            >
          </div>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  ::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 8px;
  }

  ::-webkit-scrollbar-corner {
    display: none;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 5px;
    background-color: rgba(0, 0, 0, 0.5);
    -webkit-box-shadow: 0 0 1px var(--rekki-neutral-01);
  }

  .container {
    display: flex;
    flex: 0;
    position: absolute;
    top: 69px;
    transition: all 500ms;
    -webkit-transition: all 500ms;
    flex-direction: row;
    background-color: var(--rekki-neutral-01);
    border-radius: 8px;
    z-index: 99;
  }

  .presets {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: flex-start;
    border: solid 1px var(--rk-divider-1);
    border-width: 0 1px 0 0;
    min-width: 155px;
    padding-top: 12px;
  }

  .filters {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    /* give some breathing room for the lightbox border */
    margin-bottom: 3px;
    margin-left: 3px;
  }

  .filters fieldset {
    /* these were the UA defaults (at least mine) before we applied a padding: 0 globally, so
     * restoring them for breathing room. */
    padding-top: 4.9px;
    padding-bottom: 8.75px;

    padding-left: 6px;
    padding-right: 6px;
  }

  .filters .preset-row {
    padding: 9px 6px;
  }

  .filters input[type="radio"] {
    appearance: none;

    border-radius: 50%;
    width: 15px;
    height: 15px;

    /* preserve the bg color so that the whole thing doesn't disappear when we hover */
    background-color: var(--rekki-background) !important;
    border: 2px solid #999;

    /* pre-margin reset values from the UA, very crowded without */
    margin: 3px 3px 0px 5px;
  }

  .filters input[type="radio"]:checked {
    border: 4px solid var(--rekki-dark);
  }

  .preset-row {
    display: flex;
    overflow: hidden;
    justify-content: flex-start;
    align-items: center;
    padding: 9px 18px;
    cursor: pointer;
    color: var(--rekki-transparency-dark);
  }

  .preset-row:hover {
    background-color: var(--rekki-dark);
    color: var(--rekki-light);
  }

  .calendar-container {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: flex-start;
  }

  .header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin: 28px 24px 0 24px;
    overflow: hidden;
  }

  .calendar {
    display: flex;
    flex-direction: column;
    width: 400px;
  }

  .selected-date {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 40px;
    padding: 5px 10px;
    margin-left: 4px;
    margin-right: 4px;
    border-radius: 4px;
    background-color: white;
  }

  .calendar-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    border: solid 1px var(--rk-divider-1);
    border-width: 0 0 1px 0;
    padding: 28px 10px 0 10px;
  }

  .calendar-content {
    padding: 21px 10px 0 10px;
    height: 280px;
    contain: strict;
    overflow-y: auto;
  }

  .month-container {
    display: flex;
    flex-direction: column;
    margin-top: 32px;
  }

  .month {
    font-weight: 600;
    margin-bottom: 12px;
    margin-left: 18px;
  }

  .week-row {
    display: flex;
    flex: 1;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 3px;
  }

  .footer {
    display: flex;
    align-self: flex-end;
    margin: 31px 18px 20px 0;
    overflow: hidden;
  }

  .cancel {
    margin-right: 8px;
  }
</style>
